import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';

/**
 * Quién hizo algo, en palabras, a partir de lo que guarda un `createdBy`.
 *
 * Lo guardado es de tres clases:
 * - el id de la persona (`oid` de Entra ID), que se traduce con el directorio
 *   de /api/usuarios;
 * - su correo, que es lo que guardan los `quien()` de algunas rutas;
 * - un nombre de sistema (`mcp-system`, `system:facebook`…).
 *
 * Hasta octubre de 2026 la identidad no se leía bien y todo lo hecho a mano se
 * guardó como `unknown`: eso, y lo que no se reconoce, devuelve `null` para no
 * enseñar un id que no dice nada.
 */

export interface UsuarioDirectorio {
  id: string;
  email: string;
  nombre: string | null;
}

const SISTEMA: Record<string, string | null> = {
  'mcp-system': 'Claude (MCP)',
  'MCP System User': 'Claude (MCP)',
  'system:facebook': 'Facebook',
  'tusereno.es': 'Web',
  web: 'Web',
  'clickandsign-webhook': 'Click & Sign',
  system: 'Sistema',
  sistema: 'Sistema',
  default: 'Sistema',
  unknown: null,
  desconocido: null,
  'dev-user': null,
  Developer: null,
  'Dev User': null,
};

export function nombreAutor(valor: string | null | undefined, usuarios: UsuarioDirectorio[]): string | null {
  if (!valor) return null;
  if (valor in SISTEMA) return SISTEMA[valor];
  const minusculas = valor.toLowerCase();
  const usuario = usuarios.find(u => u.id === valor || u.email?.toLowerCase() === minusculas);
  if (usuario) return usuario.nombre || usuario.email;
  return valor.includes('@') ? valor : null;
}

// Una sola petición para todas las fichas y líneas de tiempo de la pantalla.
// Se renueva pasado un rato para que entre quien haya empezado a usar el CRM.
const VIGENCIA_MS = 5 * 60 * 1000;
let directorio: { pedido: number; usuarios: Promise<UsuarioDirectorio[]> } | null = null;

function cargarDirectorio(): Promise<UsuarioDirectorio[]> {
  if (!directorio || Date.now() - directorio.pedido > VIGENCIA_MS) {
    const usuarios = api.getUsuarios().catch(() => {
      // Que el siguiente lo vuelva a intentar en vez de quedarse sin nombres.
      directorio = null;
      return [];
    });
    directorio = { pedido: Date.now(), usuarios };
  }
  return directorio.usuarios;
}

/** `autor(createdBy)` → «Jose Huéscar», «Web»… o `null` si no se sabe. */
export function useAutores(): (valor: string | null | undefined) => string | null {
  const [usuarios, setUsuarios] = useState<UsuarioDirectorio[]>([]);
  useEffect(() => {
    let vigente = true;
    cargarDirectorio().then(u => { if (vigente) setUsuarios(u); });
    return () => { vigente = false; };
  }, []);
  return useCallback((valor: string | null | undefined) => nombreAutor(valor, usuarios), [usuarios]);
}
